import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { api } from '../api';
import { LoadingSpinner } from '../components/shared';
import SelettoreCartella from '../components/SelettoreCartella';

const SFOGLIA = '__sfoglia__';
const etichetta = b => (b === 0 ? 'Generale' : String(b));

export default function CollegaCartelle({ addToast }) {
  const navigate = useNavigate();
  const [anniSel, setAnniSel] = useState(null);   // null = default del server
  const [dati, setDati] = useState(null);
  const [loading, setLoading] = useState(true);
  const [errore, setErrore] = useState('');
  const [scelte, setScelte] = useState({});       // "idCliente:bersaglio" -> percorso
  const [solo, setSolo] = useState(true);
  const [q, setQ] = useState('');
  const [saving, setSaving] = useState(false);
  const [sfoglia, setSfoglia] = useState(null);   // { chiave, inizio }

  const carica = (anni) => {
    setLoading(true); setErrore('');
    api.get('/api/cartelle/proposte' + (anni ? '?anni=' + anni.join(',') : ''))
      .then(d => {
        setDati(d);
        const s = {};
        d.righe.forEach(r => d.bersagli.forEach(b => {
          const c = r.per[b];
          if (c.stato === 'sicuro' && c.proposta) s[r.id + ':' + b] = c.proposta.percorso;
        }));
        setScelte(s);
        if (!anni) setAnniSel(d.anni);
      })
      .catch(e => setErrore(e.message))
      .finally(() => setLoading(false));
  };
  useEffect(() => { carica(null); }, []);  

  const cambiaAnno = (a) => {
    const n = anniSel.includes(a) ? anniSel.filter(x => x !== a) : [...anniSel, a].sort().reverse();
    setAnniSel(n); carica(n);
  };

  const righe = useMemo(() => {
    if (!dati) return [];
    return dati.righe.filter(r => {
      if (q && !r.nome.toLowerCase().includes(q.toLowerCase())) return false;
      if (solo && !dati.bersagli.some(b => r.per[b].stato !== 'collegato')) return false;
      return true;
    });
  }, [dati, q, solo]);

  const nSel = Object.keys(scelte).filter(k => scelte[k]).length;

  const imposta = (chiave, valore) => {
    if (valore === SFOGLIA) {
      const b = parseInt(chiave.split(':')[1], 10);
      setSfoglia({ chiave, inizio: b === 0 ? 'Clienti' : String(b) });
      return;
    }
    setScelte(p => { const n = { ...p }; if (valore) n[chiave] = valore; else delete n[chiave]; return n; });
  };

  const salva = async () => {
    const items = Object.entries(scelte).filter(([, v]) => v).map(([k, v]) => {
      const [id, b] = k.split(':'); return { id_cliente: parseInt(id, 10), anno: parseInt(b, 10), percorso: v };
    });
    if (!items.length) return;
    setSaving(true);
    try {
      const r = await api.post('/api/cartelle/salva-multiplo', { items });
      addToast(`${r.salvati} collegamenti salvati`, 'success');
      carica(anniSel);
    } catch (e) { addToast(e.message, 'error'); }
    finally { setSaving(false); }
  };

  const cella = (r, b) => {
    const c = r.per[b]; const chiave = r.id + ':' + b;
    if (c.stato === 'collegato') {
      return <span style={{ fontSize:12, color:'var(--c-green, #059669)' }} title={c.attuale}>✓ {c.attuale}</span>;
    }
    const opz = [];
    if (c.proposta) opz.push(c.proposta);
    (c.alternative || []).forEach(a => opz.push(a));
    const scelta = scelte[chiave] || '';
    const fuori = scelta && !opz.some(o => o.percorso === scelta);
    const colore = c.stato === 'sicuro' ? '#059669' : c.stato === 'verifica' ? '#D97706' : 'var(--text-faint)';
    return (
      <select className="form-control" value={scelta} onChange={e => imposta(chiave, e.target.value)}
        style={{ fontSize:12, padding:'3px 6px', minWidth:170, borderColor: scelta ? colore : undefined }}>
        <option value="">{c.stato === 'nessuno' ? '— nessuna proposta —' : '— salta —'}</option>
        {opz.map(o => <option key={o.percorso} value={o.percorso}>{o.nome} ({Math.round(o.score * 100)}%)</option>)}
        {fuori && <option value={scelta}>{scelta}</option>}
        <option value={SFOGLIA}>📂 Sfoglia OneDrive…</option>
      </select>
    );
  };

  if (loading && !dati) return <div className="page-content"><LoadingSpinner /></div>;

  return (
    <div className="page-content">
      <div className="page-header">
        <div>
          <button className="btn btn-sm" onClick={() => navigate('/clienti')} style={{ marginBottom:8 }}>← Clienti</button>
          <div className="page-title">☁ Collega cartelle OneDrive</div>
          <div className="page-subtitle">Verde = abbinamento sicuro (già selezionato) · Arancione = da verificare · le caselle vuote vengono saltate</div>
        </div>
        <button className="btn btn-primary" onClick={salva} disabled={saving || !nSel}>
          {saving ? 'Salvataggio…' : `Salva ${nSel} collegamenti`}
        </button>
      </div>

      {errore && <div style={{ padding:12, background:'#FEF2F2', border:'1px solid #FECACA', borderRadius:'var(--radius-sm)', color:'#DC2626', marginBottom:12 }}>⚠ {errore}</div>}

      {dati && (
        <div className="filters-bar" style={{ flexWrap:'wrap' }}>
          <input className="form-control" style={{ width:220 }} placeholder="Cerca cliente…" value={q} onChange={e => setQ(e.target.value)} />
          <label style={{ display:'flex', alignItems:'center', gap:6, fontSize:13 }}>
            <input type="checkbox" checked={solo} onChange={e => setSolo(e.target.checked)} /> Solo da collegare
          </label>
          <span style={{ fontSize:12, color:'var(--text-faint)', marginLeft:8 }}>Anni:</span>
          {(dati.anniDisponibili || []).slice(0, 8).map(a => (
            <label key={a} style={{ display:'flex', alignItems:'center', gap:4, fontSize:13 }}>
              <input type="checkbox" checked={(anniSel || []).includes(a)} onChange={() => cambiaAnno(a)} /> {a}
            </label>
          ))}
        </div>
      )}

      {dati && (
        <div className="card">
          <div className="table-wrap" style={{ opacity: loading ? 0.5 : 1 }}>
            <table>
              <thead><tr>
                <th>Cliente</th>
                {dati.bersagli.map(b => <th key={b}>{etichetta(b)}</th>)}
              </tr></thead>
              <tbody>
                {righe.map(r => (
                  <tr key={r.id} style={{ opacity: r.attivo ? 1 : 0.55 }}>
                    <td className="fw-500" style={{ whiteSpace:'nowrap' }}>{r.nome}</td>
                    {dati.bersagli.map(b => <td key={b}>{cella(r, b)}</td>)}
                  </tr>
                ))}
                {righe.length === 0 && <tr><td colSpan={dati.bersagli.length + 1} style={{ textAlign:'center', padding:'24px 0', color:'var(--text-faint)' }}>Nessun cliente da collegare</td></tr>}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {sfoglia && (
        <SelettoreCartella inizio={sfoglia.inizio} titolo="Scegli la cartella"
          onClose={() => setSfoglia(null)}
          onScegli={(p) => { setScelte(s => ({ ...s, [sfoglia.chiave]: p })); setSfoglia(null); }} />
      )}
    </div>
  );
}
